import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { MdRefresh, MdImage, MdSearch } from 'react-icons/md';
import { researchosService } from '../../services/researchosService';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';
import { ExtractionModal } from './PropagationPanel';

// The NEW Chatter home (Plans/chatter_revamp.md, phase 1) — one dashboard across YouTube, X and Reddit, built beside
// the classic pages (which stay untouched as the backup until the owner finalizes). House rules: mobile-first (nothing
// scrolls sideways), every number opens what is behind it, sortable tables via the shared primitive, green/red numbers.
// The chart rail is the MAIN rail (echo-filtered claim rail).
// PHONE LAYOUT (2026-10-18, owner: "what comes in the viewport on load?"): measured at 375×812 the first screen held only
// the title, filters and four count tiles — the feed began 2.3 screens down and the movers 13.6 screens down. Now one
// grid, ordered by screen: on a phone the first screen is the source switch, the MOVERS and ONE chart card (tabs for
// chatter-vs-price / volume), then the feed (5 cards, theses clamped), most discussed, and the pipeline counts last; the
// desktop keeps counts on top and movers + most discussed beside the feed. Safari: the search field is 16 px (a smaller
// field zooms the page on focus), every control is ≥ 40 px tall on a phone, taps skip the double-tap delay.
const GOLD = 'rgb(var(--brand-gold-rgb))';
const SRC = {
  youtube: { label: 'YouTube', color: '#7f9cc9' },
  x: { label: 'X', color: '#8b8f96' },
  reddit: { label: 'Reddit', color: '#e3884a' },
};

// The platforms' own logos (owner 2026-10-18), served from our site (public/brand/, sources in its README). X's mark
// is monochrome, so it swaps black ↔ white with the theme.
function SourceLogo({ s, size = 14 }) {
  const img = (src, cls = '') => <img src={src} alt="" width={size} height={size} className={`inline-block shrink-0 object-contain ${cls}`} style={{ width: size, height: size }} />;
  if (s === 'x') return <>{img('/brand/x-black.svg', 'dark:hidden')}{img('/brand/x-white.svg', 'hidden dark:inline-block')}</>;
  if (s === 'youtube' || s === 'reddit') return img(`/brand/${s}.svg`);
  return null;
}
// ONE period control for the whole page (owner 2026-10-18) — movers' change, feed, Most discussed and both charts. A
// chart never shows fewer than 30 days (CHART_RANGE, same as the API's chatter_home.CHART_RANGE).
const PERIODS = ['24h', '7d', '30d', '3M', '6M', '1Y', 'ALL'];
const CHART_RANGE = { '24h': '1M', '7d': '1M', '30d': '1M', '3M': '3M', '6M': '6M', '1Y': '1Y', ALL: 'ALL' };
const periodLabel = (p) => (p === 'ALL' ? 'All' : p);
const RANGE_LABEL = { '1M': 'last 30 days', '3M': 'last 3 months', '6M': 'last 6 months', '1Y': 'last year', ALL: 'all time' };
const periodPhrase = (p) => (p === 'ALL' ? 'all time' : p);
// Search: ticker-looking text ("NVDA", "$nvda", "NVDA, AMD") is ADDED to the picks on Enter; a sector / sub-industry
// is picked from the suggestions; anything else (@handle, r/sub, a channel name) filters the feed.
const MAX_PICKS = 5;
const tickersOf = (q) => {
  const parts = (q || '').split(/[\s,]+/).map((t) => t.trim().replace(/^\$/, '')).filter(Boolean);
  return parts.length && parts.every((t) => /^[A-Za-z][A-Za-z0-9.-]{0,5}$/.test(t)) ? parts.map((t) => t.toUpperCase()) : null;
};
const STANCE = { bullish: '#16a34a', bearish: '#dc2626', neutral: '#9ca3af' };
const BUCKET_LABEL = { day: 'daily', week: 'weekly', month: 'monthly' };
const FEED_FIRST_ON_PHONE = 5;
const nfmt = (v) => (v == null ? '—' : Number(v).toLocaleString());
const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
const signed = (v, d = 2) => (v == null ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(d)}`);
const tone = (v) => (v > 0 ? 'text-green-600 dark:text-green-400' : v < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400');

// true below Tailwind's `lg` (1024 px) — the breakpoint where the layout switches to the desktop grid
function usePhoneLayout() {
  const q = '(max-width: 1023px)';
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  return m;
}

function zscore(arr) {
  const xs = arr.filter((v) => v != null);
  if (xs.length < 2) return arr.map(() => null);
  const m = xs.reduce((a, b) => a + b, 0) / xs.length;
  const sd = Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / xs.length) || 1;
  return arr.map((v) => (v == null ? null : (v - m) / sd));
}

function Label({ children }) {
  return <div className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400">{children}</div>;
}

// A pipeline tile — always a link (drill-down rule). `soon` = its list page arrives in phase 2. Compact on a phone.
function Tile({ label, value, sub, to, soon, lead, split }) {
  const body = (
    <>
      <div className="flex items-center justify-between"><Label>{label}</Label>{!soon && <span style={{ color: GOLD }}>→</span>}</div>
      <div className={`mt-1 font-serif tabular-nums ${lead ? 'text-xl sm:text-[26px]' : 'text-xl sm:text-2xl'} text-gray-900 dark:text-gray-100`}
        style={lead ? { color: GOLD } : undefined}>{value}</div>
      {split && (
        <div className="mt-1.5 flex h-1 overflow-hidden rounded-full">
          {split.map(([k, n]) => <span key={k} style={{ flex: n || 0, background: SRC[k].color }} />)}
        </div>
      )}
      <div className="mt-1 hidden text-[11px] text-gray-400 sm:block">{soon ? `${sub} · list page in phase 2` : sub}</div>
    </>
  );
  const cls = 'flex min-h-[72px] flex-col justify-between bg-white p-3 sm:min-h-[104px] sm:p-4 dark:bg-gray-900/70';
  return soon
    ? <div className={cls} title={`${sub} · its list page arrives in phase 2`}>{body}</div>
    : <Link to={to} className={`${cls} transition-colors hover:bg-gray-50 dark:hover:bg-gray-900`}>{body}</Link>;
}

function SourceBadge({ s }) {
  const m = SRC[s] || SRC.x;
  return (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ background: `${m.color}22`, color: m.color }}><SourceLogo s={s in SRC ? s : 'x'} size={11} />{m.label}</span>
  );
}

function CompanyChip({ c }) {
  const color = STANCE[c.stance] || STANCE.neutral;
  const arrow = c.stance === 'bullish' ? '▲ ' : c.stance === 'bearish' ? '▼ ' : '';
  const text = `${arrow}${c.ticker || c.name}`;
  const cls = 'inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium';
  if (c.ticker && c.coverage === 'us_listed') {
    return <Link to={`/researchos/company/${c.ticker}`} onClick={(e) => e.stopPropagation()} className={`${cls} hover:underline`}
      style={{ background: `${color}1a`, color }}>{text}</Link>;
  }
  return <span className={`${cls} border border-dashed`} style={{ borderColor: `${color}66`, color }}
    title={c.coverage === 'research_only' ? 'Not US-listed' : 'Private or not covered'}>{text}</span>;
}

function FeedCard({ r, onOpen }) {
  return (
    <article onClick={() => onOpen(r)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r); }}
      className="flex cursor-pointer flex-col gap-2 rounded-xl border border-gray-200 p-3.5 transition-colors hover:border-brand-gold/60 sm:gap-2.5 sm:p-4 dark:border-gray-800">
      <div className="flex min-w-0 items-center gap-2 text-[11px] text-gray-400">
        <SourceBadge s={r.source_type} />
        <span className="min-w-0 truncate text-gray-600 dark:text-gray-300">{r.source_ref}</span>
        {r.published_at && <span className="shrink-0">· {new Date(r.published_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>}
        {r.images > 0 && <span className="ml-auto inline-flex shrink-0 items-center gap-1"><MdImage size={12} />{r.images}</span>}
      </div>
      {r.thesis && <p className="line-clamp-4 font-serif text-[15px] leading-relaxed text-gray-800 sm:line-clamp-none dark:text-gray-100">{r.thesis}</p>}
      <div className="flex flex-wrap items-center gap-1.5">
        {(r.companies || []).map((c, i) => <CompanyChip key={`${c.name}-${i}`} c={c} />)}
        <span className="ml-auto text-[11px] text-gray-400">{nfmt(r.claims)} claims →</span>
      </div>
    </article>
  );
}

// The page's picks (≤ 5): stocks and / or GICS groups. One colour each, in pick order.
const PICK_COLORS = ['#c9a96e', '#3b82f6', '#22c55e', '#a855f7', '#f97316'];
const itemsParam = (picks) => picks.map((p) => `${p.kind === 'ticker' ? 't' : 'g'}:${p.key}`).join(',');

function VolumeChart({ source, picks, period, phone }) {
  const range = CHART_RANGE[period] || '1M';
  const items = itemsParam(picks);
  const [v, setV] = useState(null);
  useEffect(() => {
    let live = true;
    setV(null);
    researchosService.getChatterVolume(range, source, items || undefined).then((r) => { if (live) setV(r.data); }).catch(() => {});
    return () => { live = false; };
  }, [range, source, items]);
  const compare = !!(v && v.items);                  // several picks: one line per pick (documents mentioning it)
  const rows = v?.rows || [];
  const traces = compare
    ? v.items.map((it, k) => ({ type: 'scatter', mode: 'lines', name: it.label, x: v.dates, y: it.counts,
      line: { color: PICK_COLORS[k % PICK_COLORS.length], width: 2 }, hovertemplate: `%{x}<br>${it.label}: %{y}<extra></extra>` }))
    : Object.keys(SRC).map((k) => ({
      type: 'bar', name: SRC[k].label, x: rows.map((d) => d.date), y: rows.map((d) => d[k]), marker: { color: SRC[k].color },
      hovertemplate: `%{x}<br>${SRC[k].label}: %{y}<extra></extra>` }));
  const totals = Object.fromEntries(Object.keys(SRC).map((k) => [k, rows.reduce((a, d) => a + (d[k] || 0), 0)]));
  const title = picks.length === 0 ? 'Chatter volume' : picks.length === 1 ? `${picks[0].label} mentions` : 'Mentions compared';
  return (
    <figure className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="hidden font-serif text-lg text-gray-900 lg:block dark:text-gray-100">{title}</h3>
        <span className="text-[11px] text-gray-400">{picks.length ? 'documents mentioning each pick' : 'every document'} · {RANGE_LABEL[range]}</span>
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] text-gray-400">
        {compare
          ? v.items.map((it, k) => (
            <span key={it.key} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: PICK_COLORS[k % PICK_COLORS.length] }} />{it.label} {nfmt(it.counts.reduce((a, b) => a + b, 0))}</span>))
          : Object.keys(SRC).map((k) => (
            <span key={k} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: SRC[k].color }} />{SRC[k].label} {nfmt(totals[k])}</span>))}
      </div>
      {!v ? <div className="h-[180px] animate-pulse rounded bg-gray-100 sm:h-[220px] dark:bg-gray-800/50" /> : (
        <Plot data={traces}
          layout={mergeLayout({ height: phone ? 180 : 220, barmode: 'stack', showlegend: false, bargap: 0.15,
            margin: { l: 30, r: 4, t: 4, b: 24 }, font: { size: phone ? 10 : 11 },
            xaxis: { type: 'date', tickformat: v.bucket === 'month' ? '%b %y' : '%d %b', nticks: phone ? 4 : 8 } }, isDark() ? 'dark' : 'light')}
          config={{ ...PLOTLY_CONFIG, staticPlot: phone }} style={{ width: '100%' }} useResizeHandler />
      )}
      <figcaption className="text-[11px] text-gray-400">
        By posting date (UTC), {BUCKET_LABEL[v?.bucket] || 'daily'}{CHART_RANGE[period] !== period && period !== '30d' ? ' — charts show at least 30 days' : ''}<span className="hidden sm:inline">. Backfilled history counts on the day it was posted, so long ranges also show when we backfilled; the current {v?.bucket || 'day'} is partial</span>.
      </figcaption>
    </figure>
  );
}

// One pick: its chatter line vs its price (a group's price = its ETF). Several: their chatter lines only, compared
// (owner 2026-10-18). Z-scores so differently-scaled lines share one axis.
function ChatterChart({ picks, period, phone }) {
  const range = CHART_RANGE[period] || '1M';
  const items = itemsParam(picks);
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!items) return;
    let live = true;
    setS(null); setErr(null);
    researchosService.getChatterCompare(items, range)
      .then((r) => { if (live) setS(r.data); })
      .catch(() => { if (live) setErr('Could not load the series.'); });
    return () => { live = false; };
  }, [items, range]);
  const lines = useMemo(() => (s ? s.lines.map((l) => ({ ...l, z: zscore(l.rail) })) : []), [s]);
  const zp = useMemo(() => (s && s.price && s.price.length ? zscore(s.price) : []), [s]);
  const last = (a) => [...a].reverse().find((v) => v != null);
  const single = picks.length === 1;
  const linkOf = (p) => (p.kind === 'ticker' ? `/researchos/company/${p.key}` : null);
  const hasData = lines.some((l) => l.rail.length);
  return (
    <figure className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="hidden font-serif text-lg text-gray-900 lg:block dark:text-gray-100">
          {single ? <>{linkOf(picks[0]) ? <Link to={linkOf(picks[0])} className="hover:underline">{picks[0].label}</Link> : picks[0].label} · chatter vs price</> : picks.length ? 'Chatter compared' : 'Chatter vs price'}
        </h3>
        <span className="text-[11px] text-gray-400">{RANGE_LABEL[range]}</span>
      </div>
      {!picks.length && (
        <div className="flex h-[180px] items-center justify-center rounded-lg border border-dashed border-gray-200 px-4 text-center text-sm text-gray-400 sm:h-[220px] dark:border-gray-800">
          Pick up to 5 names or sectors (movers, Most discussed, or search) to compare their chatter.
        </div>
      )}
      {picks.length > 0 && hasData && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12px]">
          {lines.map((l, k) => <span key={l.key} style={{ color: PICK_COLORS[k % PICK_COLORS.length] }}>{l.label} {signed(last(l.z))}σ</span>)}
          {single && zp.length > 0 && <span className="text-blue-500">{s.price_of} price {signed(last(zp))}σ</span>}
        </div>
      )}
      {err && <div className="text-sm text-red-500">{err}</div>}
      {picks.length > 0 && !s && !err && <div className="h-[180px] animate-pulse rounded bg-gray-100 sm:h-[220px] dark:bg-gray-800/50" />}
      {s && !hasData && <div className="text-sm text-gray-400">No chatter line for {picks.map((p) => p.label).join(', ')} yet.</div>}
      {s && hasData && (
        <Plot data={[
          ...(single && zp.length ? [{ type: 'scatter', mode: 'lines', name: `${s.price_of} price`, x: s.points, y: zp,
            line: { color: '#3b82f6', width: 2, dash: 'dash' }, hovertemplate: `%{x}<br>${s.price_of} price z %{y:.2f}<extra></extra>` }] : []),
          ...lines.map((l, k) => ({ type: 'scatter', mode: 'lines', name: l.label, x: s.points, y: l.z,
            line: { color: PICK_COLORS[k % PICK_COLORS.length], width: 2.5 }, hovertemplate: `%{x}<br>${l.label} z %{y:.2f}<extra></extra>` })),
        ]}
          layout={mergeLayout({ height: phone ? 180 : 220, showlegend: false, hovermode: 'x unified',
            margin: { l: 26, r: 4, t: 4, b: 24 }, font: { size: phone ? 10 : 11 },
            xaxis: { type: 'date', tickformat: '%d %b', nticks: phone ? 4 : 8 }, yaxis: { zeroline: true } }, isDark() ? 'dark' : 'light')}
          config={{ ...PLOTLY_CONFIG, staticPlot: phone }} style={{ width: '100%' }} useResizeHandler />
      )}
      <figcaption className="text-[11px] text-gray-400">Echo-filtered claim rail{single ? ' vs close' : 's'}, z-scores{s?.step === 'week' ? ', weekly' : ''}{CHART_RANGE[period] !== period && period !== '30d' ? ' — charts show at least 30 days' : ''}<span className="hidden sm:inline">; a sector's line weights its companies by how much each was discussed in the prior 30 days, its price is its ETF</span>.</figcaption>
    </figure>
  );
}

// Phone: ONE chart card with two tabs (two stacked charts pushed the feed 2.3 screens down). Desktop: side by side.
function Charts({ source, picks, period, phone }) {
  const [tab, setTab] = useState('price');
  if (!phone) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800"><VolumeChart source={source} picks={picks} period={period} phone={false} /></div>
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800"><ChatterChart picks={picks} period={period} phone={false} /></div>
      </div>
    );
  }
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-gray-200 p-3 dark:border-gray-800">
      <div role="tablist" aria-label="Chart" className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-900">
        {[['price', 'Chatter'], ['volume', 'Volume']].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`h-10 rounded-md text-[13px] ${tab === k ? 'bg-white font-medium text-gray-900 shadow-sm dark:bg-gray-800 dark:text-gray-100' : 'text-gray-500'}`}>{l}</button>
        ))}
      </div>
      {tab === 'price' ? <ChatterChart picks={picks} period={period} phone /> : <VolumeChart source={source} picks={picks} period={period} phone />}
    </section>
  );
}

const MOVER_COLS = [
  { key: 'ticker', type: 'string', get: (r) => r.ticker },
  { key: 'level', type: 'number', get: (r) => r.level },
  { key: 'change', type: 'number', get: (r) => r.change },
];

// Phone: a 3-column grid of tap targets (tap = chart that company). Desktop: the sortable table.
function Movers({ movers, onPick, picked = new Set(), period, phone }) {
  const { sorted, toggle, indicator } = useSortableRows(movers?.rows || [], MOVER_COLS);
  const rows = movers?.rows || [];
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3 sm:p-4 dark:border-gray-800">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-serif text-base text-gray-900 sm:text-lg dark:text-gray-100">Signal movers</h3>
        <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
          {movers?.source && movers.source !== 'all' ? <><SourceLogo s={movers.source} size={11} />{SRC[movers.source]?.label} line</> : 'all sources'} · {period === 'ALL' ? 'change since first point' : `${periodLabel(period)} change`}
        </span>
      </div>
      {phone ? (
        <div className="grid grid-cols-3 gap-1.5">
          {rows.slice(0, 6).map((r) => (
            <button key={r.ticker} type="button" onClick={() => onPick(r.ticker)} aria-pressed={picked.has(r.ticker)}
              className={`flex h-14 flex-col items-start justify-center rounded-lg border px-2.5 text-left ${picked.has(r.ticker) ? 'border-brand-gold' : 'border-gray-200 dark:border-gray-800'}`}>
              <span className="font-mono text-[13px] font-medium text-gray-900 dark:text-gray-100">{r.ticker}</span>
              <span className={`font-mono text-[12px] ${tone(r.change)}`}>{signed(r.change)}</span>
            </button>
          ))}
          {!rows.length && <div className="col-span-3 py-3 text-center text-sm text-gray-400">No moves on this line for the period.</div>}
        </div>
      ) : (
        <>
          <div className="text-[11px] text-gray-400">{movers?.as_of ? `As of ${movers.as_of} 07:30 ET · ` : ''}among the most-discussed names · click a row to chart it</div>
          <table className="w-full text-sm tabular-nums">
            <thead><tr className="border-b border-gray-200 text-[10px] uppercase tracking-[0.12em] text-gray-400 dark:border-gray-800">
              <SortableTh label="Ticker" colKey="ticker" toggle={toggle} indicator={indicator} align="left" />
              <SortableTh label="Level" colKey="level" toggle={toggle} indicator={indicator} />
              <SortableTh label={period === 'ALL' ? 'All' : periodLabel(period)} colKey="change" toggle={toggle} indicator={indicator} />
            </tr></thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.ticker} onClick={() => onPick(r.ticker)}
                  className={`cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-900 ${picked.has(r.ticker) ? 'bg-gray-50 dark:bg-gray-900' : ''}`}>
                  <td className="py-1.5 font-mono"><Link to={`/researchos/company/${r.ticker}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{r.ticker}</Link></td>
                  <td className="py-1.5 text-right font-mono text-gray-700 dark:text-gray-200">{r.level.toFixed(2)}</td>
                  <td className={`py-1.5 text-right font-mono ${tone(r.change)}`}>{signed(r.change)}</td>
                </tr>
              ))}
              {!sorted.length && <tr><td colSpan={3} className="py-3 text-center text-gray-400">No rail data yet.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function MostDiscussed({ rows, onPick, period }) {
  const max = Math.max(1, ...rows.map((r) => r.docs));
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3 sm:p-4 dark:border-gray-800">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-serif text-base text-gray-900 sm:text-lg dark:text-gray-100">Most discussed</h3>
        <span className="text-[11px] text-gray-400">documents · {periodPhrase(period)}</span>
      </div>
      <div className="flex flex-col">
        {rows.map((r) => {
          const covered = r.ticker && r.coverage === 'us_listed';
          return (
            <button key={`${r.name}-${r.ticker}`} type="button" onClick={() => covered && onPick(r.ticker)}
              className={`grid min-h-[40px] grid-cols-[88px_minmax(0,1fr)_44px] items-center gap-2 text-left text-sm ${covered ? 'hover:opacity-80' : 'cursor-default'}`}>
              <span className={`truncate ${covered ? 'font-mono text-gray-800 dark:text-gray-100' : 'text-gray-400'}`}>{covered ? r.ticker : r.name}</span>
              <span className="h-2 rounded-sm" style={{ width: `${(r.docs / max) * 100}%`, background: covered ? GOLD : '#9ca3af' }} />
              <span className="text-right font-mono text-[12px] text-gray-400">{nfmt(r.docs)}</span>
            </button>
          );
        })}
        {!rows.length && <div className="text-sm text-gray-400">Nothing in this window.</div>}
      </div>
    </section>
  );
}

// Phone: one summary line. Desktop: one chip per collector.
function Collectors() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    researchosService.getCollectors().then((r) => setRows(r.data?.collectors || [])).catch(() => {});
  }, []);
  const good = (st) => ['arrived', 'ok_nothing_new'].includes(st);
  const bad = (st) => ['not_started', 'started_no_push'].includes(st);
  const dot = (st) => (good(st) ? '#16a34a' : bad(st) ? '#dc2626' : '#9ca3af');
  if (!rows.length) return null;
  const nBad = rows.filter((c) => bad(c.state)).length;
  return (
    <>
      <Link to="/researchos/chatter" className="flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 px-3 text-[12px] text-gray-600 lg:hidden dark:border-gray-800 dark:text-gray-300">
        <span className="h-2 w-2 rounded-full" style={{ background: nBad ? '#dc2626' : '#16a34a' }} />
        Collectors: {rows.length - nBad} fine{nBad ? `, ${nBad} need a look` : ''}
        <span className="ml-auto" style={{ color: GOLD }}>Details →</span>
      </Link>
      <div className="hidden flex-wrap items-center gap-2 lg:flex">
        <Label>Collectors</Label>
        {rows.map((c) => (
          <Link key={c.collector} to="/researchos/chatter" title={c.label}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[11px] text-gray-600 hover:border-brand-gold/60 dark:border-gray-800 dark:text-gray-300">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: dot(c.state) }} />
            {c.label.split(' (')[0]} · {String(c.state || '').replace(/_/g, ' ')}
          </Link>
        ))}
      </div>
    </>
  );
}

// grid areas: phone = signal first; desktop = counts on top, movers + most discussed beside the feed
const GRID = [
  'grid grid-cols-1 gap-4',
  "[grid-template-areas:'head''filters''movers''charts''feed''discussed''pipeline''collectors']",
  'lg:grid-cols-3 lg:gap-5',
  "lg:[grid-template-areas:'head_head_head''filters_filters_filters''pipeline_pipeline_pipeline''collectors_collectors_collectors''charts_charts_charts''feed_feed_movers''feed_feed_discussed']",
  'lg:[grid-template-rows:auto_auto_auto_auto_auto_auto_1fr]',
].join(' ');

export default function ChatterNewPage() {
  const phone = usePhoneLayout();
  const [source, setSource] = useState('all');
  const [period, setPeriod] = useState('7d');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [home, setHome] = useState(null);
  const [feed, setFeed] = useState({ rows: [], next: null });
  const [showAllFeed, setShowAllFeed] = useState(false);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [picks, setPicks] = useState([]);                // ≤ 5 stocks / GICS groups — drive charts + feed
  const [note, setNote] = useState(null);
  const [suggest, setSuggest] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);
  const [err, setErr] = useState(null);
  const debounce = useRef(null);

  // The picks drive the page: both charts and the feed (documents mentioning any pick); none = the overall view.
  // Ticker-looking search text waits for Enter (it is an "add"); other text filters the feed as you type.
  const feedText = tickersOf(qLive) ? '' : qLive;
  const items = itemsParam(picks);
  const pickedTickers = useMemo(() => new Set(picks.filter((p) => p.kind === 'ticker').map((p) => p.key)), [picks]);
  const addPicks = (adds) => {
    setPicks((cur) => {
      const next = [...cur];
      for (const a of adds) {
        if (next.some((p) => p.kind === a.kind && p.key === a.key)) continue;
        if (next.length >= MAX_PICKS) { setNote(`Up to ${MAX_PICKS} picks — remove one to add ${a.label}.`); break; }
        next.push(a);
      }
      return next;
    });
  };
  const removePick = (p) => { setNote(null); setPicks((cur) => cur.filter((x) => !(x.kind === p.kind && x.key === p.key))); };
  const groupHits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2 || tickersOf(q)?.length > 1) return [];
    return (home?.groups || []).filter((g) => g.name.toLowerCase().includes(t)).slice(0, 6);
  }, [q, home]);
  // Enter takes the TOP suggestion: a matching sector / industry first, else the typed ticker(s).
  const submitSearch = () => {
    if (groupHits.length) { addPicks([{ kind: 'group', key: groupHits[0].key, label: groupHits[0].name }]); setQ(''); setSuggest(false); return; }
    const ts = tickersOf(q);
    if (ts) { addPicks(ts.map((t) => ({ kind: 'ticker', key: t, label: t }))); setQ(''); setSuggest(false); }
  };
  const addTyped = () => {
    const ts = tickersOf(q);
    if (ts) { addPicks(ts.map((t) => ({ kind: 'ticker', key: t, label: t }))); setQ(''); setSuggest(false); }
  };

  const loadHome = useCallback(() => {
    setErr(null);
    researchosService.getChatterHome({ source, period })
      .then((r) => setHome(r.data))
      .catch(() => setErr('Could not load the dashboard.'));
  }, [source, period]);

  const loadFeed = useCallback((offset = 0) => {
    setLoadingFeed(true);
    researchosService.getChatterFeed({ source, period, q: feedText, ...(items ? { items } : {}), offset })
      .then((r) => setFeed((f) => (offset ? { rows: [...f.rows, ...r.data.rows], next: r.data.next } : r.data)))
      .catch(() => setErr('Could not load the feed.'))
      .finally(() => setLoadingFeed(false));
  }, [source, period, feedText, items]);

  useEffect(() => { loadHome(); }, [loadHome]);
  useEffect(() => { loadFeed(0); setShowAllFeed(false); }, [loadFeed]);
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQLive(q.trim()), 350);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const c = home?.counts || {};
  const sourcesN = (c.channels || 0) + (c.x_handles || 0) + (c.reddit_sources || 0);
  const docsN = (c.transcripts || 0) + (c.x_docs || 0) + (c.reddit_docs || 0);
  const covPct = c.companies ? ((c.companies_covered / c.companies) * 100).toFixed(1) : '0';
  const srcCount = { all: null, youtube: c.channels, x: c.x_handles, reddit: c.reddit_sources };
  const feedRows = phone && !showAllFeed ? feed.rows.slice(0, FEED_FIRST_ON_PHONE) : feed.rows;
  const pickTicker = (t) => (pickedTickers.has(t) ? removePick({ kind: 'ticker', key: t }) : addPicks([{ kind: 'ticker', key: t, label: t }]));  // tap again = remove

  return (
    <div className={`${GRID} touch-manipulation`}>
      <header className="flex items-center justify-between gap-3 [grid-area:head] sm:items-end">
        <div className="min-w-0">
          <div className="hidden sm:block"><Label>ResearchOS · new view</Label></div>
          <h2 className="font-serif text-xl text-gray-900 sm:mt-1 sm:text-2xl dark:text-gray-100">Chatter</h2>
          <p className="mt-1 hidden max-w-xl text-sm text-gray-500 sm:block dark:text-gray-400">
            What YouTube, X and Reddit are saying about the names we track — one feed, one set of signals. Every number opens what's behind it.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[12px] text-gray-400 sm:gap-3">
          <Link to="/researchos/chatter" className="flex min-h-[40px] items-center hover:underline" style={{ color: GOLD }}>Classic</Link>
          <button type="button" onClick={() => { loadHome(); loadFeed(0); }} aria-label="Refresh"
            className="inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-md border border-gray-200 text-gray-600 hover:border-brand-gold sm:h-9 sm:w-auto sm:px-3 dark:border-gray-700 dark:text-gray-300">
            <MdRefresh size={18} /><span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </header>

      {/* ONE control bar (owner 2026-10-18): search, then the period, then the source — each drives the whole page */}
      <div className="flex flex-col gap-2 [grid-area:filters] lg:flex-row lg:flex-wrap lg:items-center lg:rounded-xl lg:border lg:border-gray-200 lg:p-2 lg:dark:border-gray-800">
        <div className="relative flex min-w-0 flex-col gap-2 lg:flex-1">
          <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-md border border-gray-200 px-2.5 lg:h-9 dark:border-gray-700">
            <MdSearch size={18} className="shrink-0 text-gray-400" aria-hidden="true" />
            <span className="sr-only">Add tickers or a sector (Enter), or filter the feed by @handle, r/subreddit or channel</span>
            {/* 16 px on a phone: iOS Safari zooms the page into any field under 16 px on focus */}
            <input value={q} onChange={(e) => { setQ(e.target.value); setSuggest(true); }} placeholder="Ticker(s), sector, @handle or r/sub"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch(); } if (e.key === 'Escape') setSuggest(false); }}
              onFocus={() => setSuggest(true)} onBlur={() => setTimeout(() => setSuggest(false), 150)}
              inputMode="search" enterKeyHint="search" autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="min-w-0 flex-1 bg-transparent text-base text-gray-800 outline-none placeholder:text-gray-400 lg:text-[13px] dark:text-gray-100" />
          </label>
          {suggest && (tickersOf(q) || groupHits.length > 0) && (
            <ul className="absolute left-0 right-0 top-11 z-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
              {groupHits.map((g) => (
                <li key={g.key}><button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { addPicks([{ kind: 'group', key: g.key, label: g.name }]); setQ(''); setSuggest(false); }}
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 px-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span className="truncate text-gray-900 dark:text-gray-100">{g.name}</span>
                  <span className="shrink-0 text-[11px] text-gray-400">{g.level === 'sector' ? 'sector' : 'industry'} · {g.members}{g.etf ? ` · ${g.etf}` : ''}</span>
                </button></li>
              ))}
              {tickersOf(q) && (
                <li><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={addTyped}
                  className="flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span className="text-gray-400">Add</span><span className="font-mono text-gray-900 dark:text-gray-100">{tickersOf(q).join(', ')}</span>
                </button></li>
              )}
            </ul>
          )}
          {picks.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {picks.map((p, k) => (
                <button key={`${p.kind}:${p.key}`} type="button" onClick={() => removePick(p)} aria-label={`Remove ${p.label}`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border px-2 text-[12px]" style={{ borderColor: PICK_COLORS[k % PICK_COLORS.length], color: PICK_COLORS[k % PICK_COLORS.length] }}>
                  <span className={p.kind === 'ticker' ? 'font-mono' : ''}>{p.label}</span><span aria-hidden="true" className="text-gray-400">✕</span>
                </button>
              ))}
              {picks.length > 1 && <button type="button" onClick={() => { setPicks([]); setNote(null); }} className="h-9 px-2 text-[12px] text-gray-400 hover:underline">Clear</button>}
            </div>
          )}
          {note && <div className="text-[12px] text-amber-600 dark:text-amber-400">{note}</div>}
        </div>
        <div role="group" aria-label="Period" className="grid grid-cols-7 gap-1 lg:flex">
          {PERIODS.map((p) => (
            <button key={p} type="button" onClick={() => setPeriod(p)} aria-pressed={period === p}
              className={`h-10 rounded-md border px-1 text-[12px] lg:h-9 lg:px-2.5 ${period === p ? 'border-brand-gold font-medium' : 'border-gray-200 text-gray-500 dark:border-gray-700'}`}
              style={period === p ? { color: GOLD } : undefined}>{periodLabel(p)}</button>
          ))}
        </div>
        <div role="group" aria-label="Source" className="grid grid-cols-4 gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-900">
          {['all', 'youtube', 'x', 'reddit'].map((k) => (
            <button key={k} type="button" onClick={() => setSource(k)} aria-pressed={source === k}
              className={`inline-flex h-10 items-center justify-center gap-1.5 rounded-md px-1.5 text-[13px] lg:h-8 lg:px-2 ${source === k ? 'bg-white font-medium text-gray-900 shadow-sm dark:bg-gray-800 dark:text-gray-100' : 'text-gray-500'}`}>
              {k !== 'all' && <SourceLogo s={k} size={15} />}{k === 'all' ? 'All' : SRC[k].label}{srcCount[k] ? <span className="ml-1 hidden font-mono text-[11px] text-gray-400 xl:inline">{srcCount[k]}</span> : null}
            </button>
          ))}
        </div>
        {err && <div className="w-full rounded-lg border border-red-200 p-3 text-sm text-red-600 dark:border-red-900/50">{err}</div>}
      </div>

      <div className="[grid-area:movers] lg:self-start">
        <Movers movers={home?.movers} onPick={pickTicker} picked={pickedTickers} period={period} phone={phone} />
      </div>

      <div className="min-w-0 [grid-area:charts]">
        <Charts source={source} picks={picks} period={period} phone={phone} />
      </div>

      <section aria-label="Live feed" className="flex min-w-0 flex-col gap-3 [grid-area:feed]">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-serif text-lg text-gray-900 sm:text-xl dark:text-gray-100">Live feed</h3>
          <span className="truncate text-[11px] text-gray-400">newest · {periodPhrase(period)}{picks.length ? ` · ${picks.map((p) => p.label).join(', ')}` : ''}{feedText ? ` · “${feedText}”` : ''}</span>
        </div>
        {feedRows.map((r) => <FeedCard key={r.doc_id} r={r} onOpen={setOpenDoc} />)}
        {!loadingFeed && !feed.rows.length && <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400 dark:border-gray-800">Nothing extracted in this window.</div>}
        {loadingFeed && <div className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800/50" />}
        {phone && !showAllFeed && feed.rows.length > FEED_FIRST_ON_PHONE ? (
          <button type="button" onClick={() => setShowAllFeed(true)}
            className="h-11 rounded-lg border border-gray-200 text-sm text-gray-500 hover:border-brand-gold dark:border-gray-700">Show {feed.rows.length - FEED_FIRST_ON_PHONE} more</button>
        ) : feed.next != null && !loadingFeed && (
          <button type="button" onClick={() => loadFeed(feed.next)}
            className="h-11 rounded-lg border border-gray-200 text-sm text-gray-500 hover:border-brand-gold dark:border-gray-700">Show more</button>
        )}
      </section>

      <div className="[grid-area:discussed] lg:self-start">
        <MostDiscussed rows={home?.most_discussed || []} onPick={pickTicker} period={period} />
      </div>

      <section aria-label="Pipeline" className="flex flex-col gap-2 [grid-area:pipeline]">
        <div className="lg:hidden"><Label>Pipeline</Label></div>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200 sm:grid-cols-4 lg:grid-cols-7 dark:border-gray-800 dark:bg-gray-800">
          <Tile label="Sources" value={nfmt(sourcesN)} lead to="/researchos/chatter" sub={`${nfmt(c.channels)} channels · ${nfmt(c.x_handles)} handles · ${nfmt(c.reddit_sources)} subreddits`} />
          <Tile label="Documents" value={nfmt(docsN)} to="/researchos/chatter"
            split={[['youtube', c.transcripts], ['x', c.x_docs], ['reddit', c.reddit_docs]]}
            sub={`${nfmt(c.transcripts)} videos · ${nfmt(c.x_docs)} X days · ${nfmt(c.reddit_docs)} Reddit days`} />
          <Tile label="Extracted" value={nfmt(c.extracted)} soon sub={`${nfmt(c.pending)} waiting · ${nfmt(c.parked_history)} parked history`} />
          <Tile label="Claims" value={nfmt(c.claims)} soon sub="grounded statements" />
          <Tile label="Companies" value={nfmt(c.companies)} to="/researchos/company" sub={`${nfmt(c.companies_covered)} US-listed (${covPct}%)`} />
          <Tile label="Relationships" value={nfmt(c.relationships)} soon sub="graph edges" />
          <Tile label="Mentions" value={nfmt(c.observations)} soon sub="company observations" />
          <div className="bg-white sm:hidden dark:bg-gray-900/70" aria-hidden="true" />
        </div>
      </section>

      <div className="[grid-area:collectors]"><Collectors /></div>

      {openDoc && <ExtractionModal docId={openDoc.doc_id} onClose={() => setOpenDoc(null)} />}
    </div>
  );
}
